import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "./api";

// ── Cache key & helpers ──────────────────────────────────────────────────────
const CACHE_KEY = "cms_site_v1";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes — re-fetches in background if stale

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return { data, fresh: Date.now() - ts < CACHE_TTL };
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

function clearCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}

// ── <head> injection ─────────────────────────────────────────────────────────
// Sets/replaces a single <meta> or <link> tag, creating it if absent.
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    const [tag] = selector.split("[");
    el = document.createElement(tag.trim());
    document.head.appendChild(el);
  }
  const [key, val] = attr.split("=");
  el.setAttribute(key.trim(), (val || "").replace(/"/g, ""));
  el.setAttribute("content", value);
}

function setLink(rel, href, attrs = {}) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function applyHeadBranding(site) {
  if (!site) return;

  const name    = site.company_name  || "";
  const tagline = site.tagline       || "";
  const desc    = site.seo_meta_description || tagline;
  const title   = site.seo_title     || (site.tagline ? `${name} | ${tagline}` : name);
  const ogImage = site.seo_og_image  || site.logo_url || "";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  // Absolute URL for images (OG requires absolute)
  const absoluteImg = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${backendUrl}${ogImage.split("?")[0]}`)
    : "";

  // ── Document title ─────────────────────────────────────────────────────────
  document.title = title;

  // ── Standard meta ──────────────────────────────────────────────────────────
  setMeta('meta[name="description"]',         'name=description',     desc);
  setMeta('meta[name="application-name"]',    'name=application-name', name);
  setMeta('meta[name="apple-mobile-web-app-title"]', 'name=apple-mobile-web-app-title', name);
  if (site.seo_meta_keywords) setMeta('meta[name="keywords"]', 'name=keywords', site.seo_meta_keywords);
  if (site.seo_robots)        setMeta('meta[name="robots"]',   'name=robots',   site.seo_robots);
  if (site.seo_author)        setMeta('meta[name="author"]',   'name=author',   site.seo_author);
  if (site.seo_theme_color)   setMeta('meta[name="theme-color"]', 'name=theme-color', site.seo_theme_color);

  // ── Verification meta tags ─────────────────────────────────────────────────
  if (site.google_site_verification)
    setMeta('meta[name="google-site-verification"]', 'name=google-site-verification', site.google_site_verification);
  if (site.bing_site_verification)
    setMeta('meta[name="msvalidate.01"]', 'name=msvalidate.01', site.bing_site_verification);

  // ── Canonical URL ──────────────────────────────────────────────────────────
  if (site.canonical_url || site.website_url) {
    const canon = site.canonical_url || site.website_url;
    setLink("canonical", canon + window.location.pathname);
  }

  // ── Open Graph ─────────────────────────────────────────────────────────────
  setMeta('meta[property="og:title"]',        'property=og:title',       site.seo_og_title || title);
  setMeta('meta[property="og:description"]',  'property=og:description', site.seo_og_description || desc);
  setMeta('meta[property="og:site_name"]',    'property=og:site_name',   name);
  setMeta('meta[property="og:type"]',         'property=og:type',        "website");
  if (absoluteImg) setMeta('meta[property="og:image"]', 'property=og:image', absoluteImg);
  if (site.website_url || site.canonical_url)
    setMeta('meta[property="og:url"]', 'property=og:url', (site.canonical_url || site.website_url) + window.location.pathname);

  // ── Twitter Card ───────────────────────────────────────────────────────────
  setMeta('meta[name="twitter:card"]',        'name=twitter:card',        "summary_large_image");
  setMeta('meta[name="twitter:title"]',       'name=twitter:title',       site.seo_twitter_title || site.seo_og_title || title);
  setMeta('meta[name="twitter:description"]', 'name=twitter:description', site.seo_twitter_description || site.seo_og_description || desc);
  const twitterImg = site.seo_twitter_image || ogImage;
  if (twitterImg) {
    const absTwImg = twitterImg.startsWith("http") ? twitterImg : `${backendUrl}${twitterImg}`;
    setMeta('meta[name="twitter:image"]', 'name=twitter:image', absTwImg);
  }

  // ── Favicon & icons ────────────────────────────────────────────────────────
  const faviconSrc = site.favicon_url || site.logo_url || "";
  if (faviconSrc) {
    const absIcon = faviconSrc.startsWith("http")
      ? faviconSrc
      : `${backendUrl}${faviconSrc}`;
    const iconUrl = absIcon.includes("?") ? absIcon : `${absIcon}?v=${site.updated_at || "1"}`;
    setLink("icon",             iconUrl, { type: "image/x-icon" });
    setLink("shortcut icon",    iconUrl);
    // Apple touch icon — prefer dedicated field
    const appleSrc = site.apple_touch_icon_url || faviconSrc;
    const absApple = appleSrc.startsWith("http") ? appleSrc : `${backendUrl}${appleSrc}`;
    setLink("apple-touch-icon", absApple);
  }

  // ── JSON-LD Organization ───────────────────────────────────────────────────
  const existingLd = document.head.querySelector('script[type="application/ld+json"]');
  const ldScript = existingLd || document.createElement("script");
  ldScript.type = "application/ld+json";
  ldScript.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": site.org_type || "Organization",
    "name": name,
    "description": desc,
    "url": site.website_url || window.location.origin,
    ...(absoluteImg ? { "logo": absoluteImg } : {}),
    ...(site.schema_logo_url ? { "logo": site.schema_logo_url.startsWith("http") ? site.schema_logo_url : `${backendUrl}${site.schema_logo_url}` } : {}),
    ...(site.contact_email ? { "email": site.contact_email } : {}),
    ...(site.contact_phone ? { "telephone": site.contact_phone } : {}),
    ...(site.address ? { "address": site.address } : {}),
  });
  if (!existingLd) document.head.appendChild(ldScript);

  // ── Google Analytics (GA4) ─────────────────────────────────────────────────
  if (site.google_analytics_id && !document.getElementById("ga-script")) {
    const gaId = site.google_analytics_id;
    const s1 = document.createElement("script");
    s1.id = "ga-script";
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.id = "ga-inline";
    s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    document.head.appendChild(s2);
  }

  // ── Google Tag Manager ─────────────────────────────────────────────────────
  if (site.google_tag_manager_id && !document.getElementById("gtm-script")) {
    const gtmId = site.google_tag_manager_id;
    const s = document.createElement("script");
    s.id = "gtm-script";
    s.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(s);
  }

  // ── Facebook Pixel ─────────────────────────────────────────────────────────
  if (site.facebook_pixel_id && !document.getElementById("fb-pixel")) {
    const pixelId = site.facebook_pixel_id;
    const s = document.createElement("script");
    s.id = "fb-pixel";
    s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
const CmsCtx = createContext(null);

export function CmsProvider({ children }) {
  // Bootstrap from localStorage synchronously — eliminates FOUC on repeat visits
  const cached = readCache();
  const [site,    setSite]    = useState(cached?.data ?? null);
  const [home,    setHome]    = useState(null);
  const [about,   setAbout]   = useState(null);
  const [contact, setContact] = useState(null);
  // ready = true once we have site data (from cache OR network)
  const [ready, setReady]     = useState(!!cached?.data);
  const fetchedRef            = useRef(false);

  // Apply head branding immediately if we have cached data
  useEffect(() => {
    if (cached?.data) applyHeadBranding(cached.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (force = false) => {
    // Skip network fetch if cache is fresh and we're not forcing
    if (!force && cached?.fresh && fetchedRef.current) return;
    fetchedRef.current = true;
    try {
      const [s, h, a, c] = await Promise.all([
        api.get("/cms/site"),
        api.get("/cms/home"),
        api.get("/cms/about"),
        api.get("/cms/contact"),
      ]);
      setSite(s.data);
      setHome(h.data);
      setAbout(a.data);
      setContact(c.data);
      writeCache(s.data);
      applyHeadBranding(s.data);
      setReady(true);
    } catch {
      // Network failed — still mark ready so the page renders with cache/defaults
      setReady(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // On admin save — force refresh and clear stale cache
  const reload = useCallback(() => {
    clearCache();
    fetchedRef.current = false;
    load(true);
  }, [load]);

  return (
    <CmsCtx.Provider value={{ site, home, about, contact, ready, reload }}>
      {children}
    </CmsCtx.Provider>
  );
}

export const useCms = () =>
  useContext(CmsCtx) || { site: null, home: null, about: null, contact: null, ready: false };
